import {
  DRAFT_MAX_AGE_DAYS,
  type AbsenceReason,
  type AuditAction,
  type AuditChangeField,
  type AuditEntityType,
  type CorrectionTarget,
  type DraftPurgeBasis,
  type DutyEventType,
} from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [ANALYTICS §8] THE WORDS THE AUDIT LOG PAGE READS ITS ROWS WITH.
 *
 *  ⚠️ EVERY TABLE IS A `Record` OVER A SHARED TUPLE, SO A MISSING WORD IS A COMPILE ERROR. The
 *  vocabulary grows by appending to `AUDIT_ACTIONS` in `packages/shared`, and the page renders
 *  whatever the API returns — a label table that could fall through would print a raw enum member
 *  for the one row nobody wrote words for (divergence #84's rule, one screen over).
 *
 *  ⚠️ `AUDIT_ACTION_ENTITY` IS FOR GROUPING, NOT A CLAIM ABOUT THE ROW. The action filter is grouped
 *  by the kind of thing an act is about, and changing the "What" filter drops the actions that do
 *  not belong to it. It is transcribed from the writers (`recordAudit` call sites), and three
 *  actions key on a DIFFERENT entity from their name — `ALLOCATION_DELEGATED` on the order,
 *  `OFFICER_DIRECTORY_VIEWED` and `ARRANGEMENTS_RESET` on an org unit — so it is a table rather
 *  than a prefix parse, [C2a §5a]'s "never parse the prefix" rule.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const AUDIT_ENTITY_TYPE_LABEL: Record<AuditEntityType, string> = {
  UPLOADED_ORDER: 'Uploaded order',
  PLACE: 'Place',
  POLICY_VERSION: 'Policy version',
  ARRANGEMENT: 'Arrangement',
  STANDING_ORDER: 'Round the clock order',
  DEPLOYMENT: 'Duty',
  ORG_UNIT: 'Org unit',
  POST_TYPE: 'Post type',
  VEHICLE_TYPE: 'Vehicle type',
  POLICY: 'Policy',
  OFFICER: 'Officer',
  DASHBOARD_LAYOUT: 'Dashboard layout',
  SETTING: 'Setting',
  /* [P7B §2] A template set, one of its templates, and an approved run that creates or publishes from them. */
  TEMPLATE_SET: 'Template set',
  TEMPLATE: 'Template',
  TEMPLATE_RUN: 'Template run',
};

export const AUDIT_ACTION_LABEL: Record<AuditAction, string> = {
  PLACE_MERGED: 'Places merged',
  POLICY_VERSION_PUBLISHED: 'Policy version published',
  ARRANGEMENT_PUBLISHED: 'Arrangement published',
  ARRANGEMENT_RESTAMPED: 'Arrangement re-stamped',
  ARRANGEMENT_ARCHIVED: 'Arrangement archived',
  ARRANGEMENT_RESTORED: 'Arrangement restored',
  ARRANGEMENT_UNPUBLISHED: 'Arrangement taken back to draft',
  ARRANGEMENT_ASSOCIATED_UNITS_CHANGED: 'Associated units changed',
  ASSIGNMENT_NAMED: 'Officer named',
  ASSIGNMENT_REMOVED: 'Officer removed',
  ASSIGNMENT_SWAPPED: 'Officer swapped',
  ALLOCATION_COMPLETED: 'Share marked complete',
  DUTY_NOTIFICATION_SENT: 'Duty notification sent',
  /* [C5c §5g] A commander's marking for somebody else, and its correction. */
  ATTENDANCE_ABSENCE_RECORDED: 'Officer marked absent by a commander',
  ATTENDANCE_MARKED_FOR_OFFICER: 'Attendance marked for an officer by a commander',
  ATTENDANCE_MARKING_CORRECTED: 'Attendance marking corrected',
  ALLOCATION_DELEGATED: 'Allocation delegated',
  /* [C6a S1b] A division answering a command position it was asked for. */
  COMMAND_SEAT_HANDED_DOWN: 'Command position handed to a unit',
  COMMAND_SEAT_NAMED: 'Officer named to a command position',
  COMMAND_SEAT_CLEARED: 'Command position cleared',
  STANDING_ORDER_CREATED: 'Round the clock order created',
  STANDING_ORDER_UPDATED: 'Round the clock order updated',
  STANDING_ORDER_ENDED: 'Round the clock order ended',
  ORG_UNIT_IMPORTED: 'Org unit imported',
  ORG_UNIT_IMPORT_UPDATED: 'Org unit updated by import',
  POST_TYPE_ARCHIVED: 'Post type archived',
  POST_TYPE_RESTORED: 'Post type restored',
  VEHICLE_TYPE_ARCHIVED: 'Vehicle type archived',
  VEHICLE_TYPE_RESTORED: 'Vehicle type restored',
  STANDING_ORDER_ARCHIVED: 'Round the clock order archived',
  STANDING_ORDER_RESTORED: 'Round the clock order restored',
  ARRANGEMENT_PURGED: 'Arrangement purged',
  OFFICER_PHONE_REVEALED: 'Phone number revealed',
  OFFICER_IMPORTED: 'Officer imported',
  OFFICER_IMPORT_UPDATED: 'Officer updated by import',
  OFFICER_DIRECTORY_VIEWED: 'Directory viewed',
  /* ⚠️ [C5 §7b] A READ, audited because the sheet now carries every named officer's number. */
  ORDER_SHEET_VIEWED: 'Order sheet viewed',
  OFFICER_CALL_INITIATED: 'Call started',
  OFFICER_TRAIL_VIEWED: 'Officer trail viewed',
  OFFICER_IDENTITY_CORRECTED: 'Officer identity corrected',
  OFFICER_PHONE_MASKED: 'Phone number masked',
  OFFICER_PURGED: 'Officer purged',
  DASHBOARD_LAYOUT_CONFIGURED: 'Dashboard layout saved',
  DASHBOARD_LAYOUT_RESET: 'Dashboard layout reset',
  ARRANGEMENTS_RESET: 'Arrangements reset',
  SETTING_CHANGED: 'Setting changed',
  ARRANGEMENT_ATTACHMENT_REMOVED: 'Attachment removed',
  UPLOADED_ORDER_REMOVED: 'Uploaded order removed',
  ORG_UNIT_CREATED: 'Org unit created',
  ORG_UNIT_UPDATED: 'Org unit edited',
  ORG_UNIT_REPARENTED: 'Org unit moved',
  OFFICER_CREATED: 'Officer created',
  OFFICER_UPDATED: 'Officer edited',
  OFFICER_TRANSFERRED: 'Officer transferred',
  POST_TYPE_CREATED: 'Post type created',
  POST_TYPE_UPDATED: 'Post type edited',
  POLICY_CREATED: 'Policy created',
  POLICY_UPDATED: 'Policy edited',
  POLICY_DRAFT_OPENED: 'Policy draft opened',
  POLICY_DRAFT_UPDATED: 'Policy draft edited',
  /* [P7B §2] The import — one row for the set and one per template it created. */
  TEMPLATE_SET_IMPORTED: 'Template set imported',
  TEMPLATE_IMPORTED: 'Template imported',
  /* [P7B §3a] A decision on a code overwrites its row, so this is the record of what it was. */
  TEMPLATE_DECISION_RECORDED: 'Template decision recorded',
  TEMPLATE_CALENDAR_SET: 'Festival dates set',
  TEMPLATE_RUN_APPROVED: 'Template run approved',
  /* [P7B §6b] A running order taken back to draft by a master super admin. */
  ARRANGEMENT_REVERTED_TO_DRAFT: 'Running arrangement taken back to draft',
};

export const AUDIT_ACTION_ENTITY: Record<AuditAction, AuditEntityType> = {
  PLACE_MERGED: 'PLACE',
  POLICY_VERSION_PUBLISHED: 'POLICY_VERSION',
  ARRANGEMENT_PUBLISHED: 'ARRANGEMENT',
  ARRANGEMENT_RESTAMPED: 'ARRANGEMENT',
  ARRANGEMENT_ARCHIVED: 'ARRANGEMENT',
  ARRANGEMENT_RESTORED: 'ARRANGEMENT',
  ARRANGEMENT_UNPUBLISHED: 'ARRANGEMENT',
  ARRANGEMENT_ASSOCIATED_UNITS_CHANGED: 'ARRANGEMENT',
  ASSIGNMENT_NAMED: 'DEPLOYMENT',
  ASSIGNMENT_REMOVED: 'DEPLOYMENT',
  ASSIGNMENT_SWAPPED: 'DEPLOYMENT',
  ALLOCATION_COMPLETED: 'DEPLOYMENT',
  DUTY_NOTIFICATION_SENT: 'DEPLOYMENT',
  ATTENDANCE_ABSENCE_RECORDED: 'DEPLOYMENT',
  ATTENDANCE_MARKED_FOR_OFFICER: 'DEPLOYMENT',
  ATTENDANCE_MARKING_CORRECTED: 'DEPLOYMENT',
  /* ⚠️ ON THE ORDER, not the duty: a delegation reshapes the order's allocation lines. */
  ALLOCATION_DELEGATED: 'ARRANGEMENT',
  /* ⚠️ [C6a S1b] ON THE ORDER too: a seat is a duty-point position, and one name reaches every open night. */
  COMMAND_SEAT_HANDED_DOWN: 'ARRANGEMENT',
  COMMAND_SEAT_NAMED: 'ARRANGEMENT',
  COMMAND_SEAT_CLEARED: 'ARRANGEMENT',
  STANDING_ORDER_CREATED: 'STANDING_ORDER',
  STANDING_ORDER_UPDATED: 'STANDING_ORDER',
  STANDING_ORDER_ENDED: 'STANDING_ORDER',
  ORG_UNIT_IMPORTED: 'ORG_UNIT',
  ORG_UNIT_IMPORT_UPDATED: 'ORG_UNIT',
  POST_TYPE_ARCHIVED: 'POST_TYPE',
  POST_TYPE_RESTORED: 'POST_TYPE',
  VEHICLE_TYPE_ARCHIVED: 'VEHICLE_TYPE',
  VEHICLE_TYPE_RESTORED: 'VEHICLE_TYPE',
  STANDING_ORDER_ARCHIVED: 'STANDING_ORDER',
  STANDING_ORDER_RESTORED: 'STANDING_ORDER',
  ARRANGEMENT_PURGED: 'ARRANGEMENT',
  OFFICER_PHONE_REVEALED: 'OFFICER',
  OFFICER_IMPORTED: 'OFFICER',
  OFFICER_IMPORT_UPDATED: 'OFFICER',
  /* ⚠️ ON THE UNIT whose roster was opened — one row per list view, not per officer ([C1g §2e]). */
  OFFICER_DIRECTORY_VIEWED: 'ORG_UNIT',
  /* The order that was printed — so `(entity_type, entity_id)` answers "who printed this?". */
  ORDER_SHEET_VIEWED: 'ARRANGEMENT',
  OFFICER_CALL_INITIATED: 'OFFICER',
  /* [ANALYTICS §6] On the officer whose trail was opened; the viewer is the actor, and the context is empty. */
  OFFICER_TRAIL_VIEWED: 'OFFICER',
  OFFICER_IDENTITY_CORRECTED: 'OFFICER',
  OFFICER_PHONE_MASKED: 'OFFICER',
  OFFICER_PURGED: 'OFFICER',
  DASHBOARD_LAYOUT_CONFIGURED: 'DASHBOARD_LAYOUT',
  DASHBOARD_LAYOUT_RESET: 'DASHBOARD_LAYOUT',
  /* ⚠️ ON THE ROOT unit — the act is commissionerate-wide rather than about one order. */
  ARRANGEMENTS_RESET: 'ORG_UNIT',
  SETTING_CHANGED: 'SETTING',
  ARRANGEMENT_ATTACHMENT_REMOVED: 'ARRANGEMENT',
  UPLOADED_ORDER_REMOVED: 'UPLOADED_ORDER',
  ORG_UNIT_CREATED: 'ORG_UNIT',
  ORG_UNIT_UPDATED: 'ORG_UNIT',
  ORG_UNIT_REPARENTED: 'ORG_UNIT',
  OFFICER_CREATED: 'OFFICER',
  OFFICER_UPDATED: 'OFFICER',
  OFFICER_TRANSFERRED: 'OFFICER',
  POST_TYPE_CREATED: 'POST_TYPE',
  POST_TYPE_UPDATED: 'POST_TYPE',
  POLICY_CREATED: 'POLICY',
  POLICY_UPDATED: 'POLICY',
  POLICY_DRAFT_OPENED: 'POLICY',
  POLICY_DRAFT_UPDATED: 'POLICY',
  /*
   * ⚠️ [P7B §2/§3a] FILED UNDER THE ENTITY THE WRITER USES. A decision and a festival calendar are
   * the SET's (a decision applies to every template using the code), the import's header row is the
   * set's too, and only a created template keys on the template itself.
   */
  TEMPLATE_SET_IMPORTED: 'TEMPLATE_SET',
  TEMPLATE_IMPORTED: 'TEMPLATE',
  TEMPLATE_DECISION_RECORDED: 'TEMPLATE_SET',
  TEMPLATE_CALENDAR_SET: 'TEMPLATE_SET',
  /* ⚠️ The run's own id — its items are overwritten as it advances, so this row is the record of the approval. */
  TEMPLATE_RUN_APPROVED: 'TEMPLATE_RUN',
  ARRANGEMENT_REVERTED_TO_DRAFT: 'ARRANGEMENT',
};

/**
 * The diff fields, by the name an operator would use. `AuditChange` renders a key it has no label for
 * as a leftover rather than guessing, so every field a writer can record must be here — which the
 * `Record` makes a compile error rather than a review note.
 */
export const AUDIT_FIELD_LABEL: Record<AuditChangeField, string> = {
  name: 'Name',
  shortCode: 'Short code',
  type: 'Type',
  subType: 'Sub-type',
  elevation: 'Acts at',
  parent: 'Parent unit',
  phone: 'Phone',
  email: 'Email',
  serviceNumber: 'Service number',
  photoUrl: 'Photo',
  accessRole: 'Access',
  orgUnit: 'Posting',
  rank: 'Rank',
  shortName: 'Short name',
  displayOrder: 'Display order',
  eligibleRanks: 'Eligible ranks',
  description: 'Description',
  postTypes: 'Post columns',
  vehicleTypes: 'Vehicle columns',
  effectiveFrom: 'Effective from',
  commandRoles: 'Command roles',
};

/**
 * ⚠️ The CONTEXT keys the server names (`contextNames`) whose own spelling reads as an id — `Org unit
 * id: CD · Central Division` says a thing the line does not show. Keys absent here (`before`, `after`,
 * `addressees`) already read as words.
 */
export const AUDIT_CONTEXT_KEY_LABEL: Readonly<Record<string, string>> = {
  orgUnitId: 'Unit',
  divisionOrgUnitId: 'Division',
  deploymentId: 'Duty',
  /* [C5c §5g] Who a commander marked — resolved to a name by the server, like `before`/`after`. */
  subjectOfficerId: 'Officer',
  /* [C5c §8] Which door destroyed a draft — the one key on a purge row that says a RULE did it. */
  basis: 'Deleted',
  /*
   * [C6a review] An un-publish row: whether a commander a DIVISION named was discarded with it. The
   * seats go with no row of their own each, so this is the one place the log says so. `Yes`/`No`.
   */
  commandSeatsNamed: 'Division-named commanders discarded',
};

/**
 * ⚠️ [C5c §8] A PURGE ROW'S `basis`, IN WORDS. The row's actor is the order's CREATOR when the sweep
 * deleted it — `audit_logs` needs a real officer and the rule is not one — so without these words a
 * creator's name would sit beside a raw `DRAFT_AGE` and read as a deletion they made by hand. A
 * `Record` over the shared tuple: a fourth door is a compile error here, not a raw code on the page.
 * The key is used by `ARRANGEMENT_PURGED` alone, so it joins `AuditChange`'s global enum table.
 *
 * ⚠️ `ARCHIVE_EXPIRED` NAMES NO NUMBER. The Archive's window is the api's `PURGE_AFTER_DAYS`, frozen
 * onto each row's `purge_at`, and `DRAFT_MAX_AGE_DAYS` is the LIVE draft's clock — two constants that
 * are both thirty today, kept apart on purpose (`lifecycle.schema.ts`). Interpolating the second into
 * a sentence about the first is the conflation that separation exists to prevent.
 */
export const DRAFT_PURGE_BASIS_WORD: Record<DraftPurgeBasis, string> = {
  MANUAL: 'By hand, from the Archive',
  ARCHIVE_EXPIRED: 'Automatically — its time in the Archive ran out',
  DRAFT_AGE: `Automatically — not published within ${String(DRAFT_MAX_AGE_DAYS)} days of being started`,
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §5g] AN ATTENDANCE ROW'S CODES, IN WORDS — AND SCOPED TO THOSE THREE ACTIONS.
 *
 *  The writer stores ids and CODES only (`ABSENT`, `NO_SHOW`, `PRESENT`), and `AuditChange`'s own
 *  rule is that a stored enum member reads in words, never raw. But the keys are SHARED spellings —
 *  `type` on an org unit row is an org-unit TYPE and `AuditChange` already words it as one — so the
 *  words here apply to these actions alone rather than joining the global per-field table.
 *
 *  ⚠️ `locationId` IS HIDDEN ON THEM, because it is an ID: the stop a Mark Reached named is a minted
 *  8-hex handle, not an ObjectId, so the reader's shape test would print it as data. "An id is never
 *  data" — the stop's own name is on the duty, which the row links to.
 *
 *  Every table is a `Record` over the shared tuple, so a fourth reason or a fifth event type is a
 *  compile error here rather than a raw code on the page. The absence words are Duty Mitra's own
 *  (`duty.absentReason.*`), so a commander and an auditor read one reason one way.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ATTENDANCE_AUDIT_ACTIONS: ReadonlySet<AuditAction> = new Set<AuditAction>([
  'ATTENDANCE_ABSENCE_RECORDED',
  'ATTENDANCE_MARKED_FOR_OFFICER',
  'ATTENDANCE_MARKING_CORRECTED',
]);

const MARKING_WORD: Record<DutyEventType, string> = {
  START_DUTY: 'Start duty',
  REACHED: 'Mark reached',
  STAND_DOWN: 'Stand down',
  ABSENT: 'Absent',
};

export const ATTENDANCE_CONTEXT_KEY_LABEL: Readonly<Record<string, string>> = {
  type: 'Marking',
  fromType: 'Withdrawn marking',
  to: 'Corrected to',
  reason: 'Reason',
};

export const ATTENDANCE_CONTEXT_WORDS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  type: MARKING_WORD,
  fromType: MARKING_WORD,
  to: { PRESENT: 'Present', ABSENT: 'Absent', UNMARKED: 'No marking' } satisfies Record<CorrectionTarget, string>,
  reason: {
    NO_SHOW: 'Did not report',
    OTHER_DUTY: 'On other duty',
    LEAVE: 'Leave or sick',
  } satisfies Record<AbsenceReason, string>,
};

/** Context keys an attendance row carries that are ids by NAME rather than by shape. */
export const ATTENDANCE_HIDDEN_CONTEXT_KEYS: ReadonlySet<string> = new Set(['locationId']);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C6a review] A COMMAND SEAT ROW'S MINTED HANDLES ARE IDS, AND AN ID IS NEVER DATA — [C5c §5g]'s
 *  `locationId` rule, for the three `COMMAND_SEAT_*` actions.
 *
 *  An ask id and a duty point id are 8-hex handles the server MINTS (`mintOne`), not ObjectIds, so
 *  `AuditChange`'s shape test printed them as "Ask id: 4f2a9c1b". They are hidden BY NAME on these
 *  actions; the row links to its order, where the duty point and the role are in words. `targets`
 *  pairs each moved position's ask id with the code it went to (`4f2a9c1b=CD · TALA`), and only the
 *  CODE is for a reader: it is worded as "Handed to CD · TALA". The ObjectId keys beside them —
 *  `seatId`, `seatIds`, `addressees` — are already hidden by shape, or named by the server.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const COMMAND_SEAT_AUDIT_ACTIONS: ReadonlySet<AuditAction> = new Set<AuditAction>([
  'COMMAND_SEAT_HANDED_DOWN',
  'COMMAND_SEAT_NAMED',
  'COMMAND_SEAT_CLEARED',
]);

/*
 * ⚠️ [C6a review] `commandKey` TOO. It is `R:incharge` — a handle the server derives from the role,
 * which the log printed as "Command key: R:incharge" with no role word on the row at all. The three
 * writers now carry `role`, the label as the fill families resolve it, and that is what is shown.
 */
export const COMMAND_SEAT_HIDDEN_CONTEXT_KEYS: ReadonlySet<string> = new Set([
  'askId',
  'askIds',
  'commandKey',
  'dutyPointId',
]);

export const COMMAND_SEAT_CONTEXT_KEY_LABEL: Readonly<Record<string, string>> = {
  role: 'Role',
  targets: 'Handed to',
};

/** `4f2a9c1b=CD · TALA,9e1d…=CD` → `CD · TALA, CD` — the codes, without the handles they were keyed on. */
export const commandSeatTargetWords = (value: string): string =>
  value
    .split(',')
    .map((pair) => pair.slice(pair.indexOf('=') + 1))
    .join(', ');

/** The action filter's option groups: one group per entity type, in the vocabulary's own order. */
export const auditActionGroups = (
  actions: readonly AuditAction[],
  entityType?: AuditEntityType,
): { label: string; options: { value: AuditAction; label: string }[] }[] => {
  const groups = new Map<AuditEntityType, AuditAction[]>();
  for (const action of actions) {
    const entity = AUDIT_ACTION_ENTITY[action];
    if (entityType !== undefined && entity !== entityType) continue;
    groups.set(entity, [...(groups.get(entity) ?? []), action]);
  }
  return [...groups].map(([entity, members]) => ({
    label: AUDIT_ENTITY_TYPE_LABEL[entity],
    options: members.map((action) => ({ value: action, label: AUDIT_ACTION_LABEL[action] })),
  }));
};
